import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../aws/AuthContext';

const API_URL = `${process.env.REACT_APP_API_URL}/v1-bodegas`;

export const useGetBodegas = () => {
    const { getJwtToken } = useAuth();

    return useQuery({
        queryKey: ['bodegas'],
        queryFn: async () => {
            const token = await getJwtToken();
            const res = await fetch(API_URL, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Error al cargar bodegas');
            return res.json();
        },
        staleTime: 1000 * 60 * 5,
    });
};

export const useSaveBodega = () => {
    const queryClient = useQueryClient();
    const { getJwtToken } = useAuth();

    return useMutation({
        mutationFn: async (payload) => {
            const token = await getJwtToken();
            const res = await fetch(API_URL, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error('Error al guardar la bodega');
            return res.json();
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['bodegas'] }),
    });
};

export const useTrasladarStock = () => {
    const queryClient = useQueryClient();
    const { getJwtToken } = useAuth();

    return useMutation({
        mutationFn: async (payload) => {
            const token = await getJwtToken();
            const res = await fetch(`${API_URL}?action=traslado`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || 'Error en el traslado');
            }
            return res.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['bodegas'] });
            queryClient.invalidateQueries({ queryKey: ['historial_inventario'] });
            queryClient.invalidateQueries({ queryKey: ['stock_bodega'] });
        },
    });
};

export const useGetStockBodega = (bodegaId) => {
    const { getJwtToken } = useAuth();

    return useQuery({
        queryKey: ['stock_bodega', bodegaId],
        queryFn: async () => {
            if (!bodegaId) return [];

            const token = await getJwtToken();
            const res = await fetch(`${API_URL}?bodega_id=${bodegaId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Error al cargar stock de la bodega');
            return res.json();
        },
        enabled: !!bodegaId,
        staleTime: 1000 * 60 * 2,
    });
};

export const useLiquidarRuta = () => {
    const queryClient = useQueryClient();
    const { getJwtToken } = useAuth();

    return useMutation({
        mutationFn: async (payload) => {
            const token = await getJwtToken();
            const res = await fetch(`${API_URL}?action=liquidacion`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || 'Error procesando la liquidación');
            }
            return res.json();
        },
        onSuccess: () => {
            // Actualiza todo el inventario visual de la app
            queryClient.invalidateQueries({ queryKey: ['bodegas'] });
            queryClient.invalidateQueries({ queryKey: ['historial_inventario'] });
            queryClient.invalidateQueries({ queryKey: ['stock_bodega'] });
            queryClient.invalidateQueries({ queryKey: ['productos'] });
        },
    });
};